import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Loader2, Send, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { qualityNcrAPI } from '@/lib/api';

export const LegacyImportButton = ({ kind, onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = (await qualityNcrAPI.legacyImport(kind, file)).data;
      toast.success(`Imported ${r.imported} old rows${r.skipped_duplicates ? ` (${r.skipped_duplicates} already in the app, skipped)` : ''}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); } finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  return (
    <>
      <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`legacy-import-input-${kind}`} />
      <Button variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={busy} data-testid={`legacy-import-btn-${kind}`}>
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Import old register</Button>
    </>
  );
};

const split = (s) => s.split(/[;,\n]/).map((x) => x.trim()).filter(Boolean);

export const ReplyEmailDialog = ({ complaint, open, onClose, onSent }) => {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ to: '', cc: '', subject: '', message: '', attach_8d: true, attachment_ids: [] });
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (!open) return;
    qualityNcrAPI.replyDraft(complaint.id).then(({ data }) => {
      setD(data);
      setF({ to: data.to.join(', '), cc: data.cc.join(', '), subject: data.subject, message: data.message, attach_8d: data.has_8d, attachment_ids: [] });
    }).catch(() => toast.error('Could not prepare the email'));
  }, [open, complaint.id]);
  const toggleAtt = (id) => setF((x) => ({ ...x, attachment_ids: x.attachment_ids.includes(id) ? x.attachment_ids.filter((a) => a !== id) : [...x.attachment_ids, id] }));
  const send = async () => {
    setSending(true);
    try {
      const r = (await qualityNcrAPI.sendReply(complaint.id, { ...f, to: split(f.to), cc: split(f.cc) })).data;
      toast.success(r.message); onSent();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); } finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reply-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4" />Email correction &amp; containment reply</DialogTitle>
          <DialogDescription>Sending fills the 7-day customer response date (if empty) with today.</DialogDescription>
        </DialogHeader>
        {!d ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : (
          <div className="space-y-3">
            <div><Label className="text-xs">To (customer)</Label><Input value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} placeholder="customer@company.com" data-testid="reply-to-input" /></div>
            <div><Label className="text-xs">CC</Label><Input value={f.cc} onChange={(e) => setF({ ...f, cc: e.target.value })} data-testid="reply-cc-input" /></div>
            <div><Label className="text-xs">Subject</Label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="reply-subject-input" /></div>
            <div><Label className="text-xs">Message</Label><Textarea rows={12} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="reply-message-input" /></div>
            <div className="space-y-1.5">
              <Label className="text-xs">Attachments</Label>
              <label className="flex items-center gap-2 text-xs"><Checkbox checked={f.attach_8d} disabled={!d.has_8d} onCheckedChange={(v) => setF({ ...f, attach_8d: !!v })} data-testid="reply-attach-8d-checkbox" />
                8D report (Word, F/QA/15){!d.has_8d && <span className="text-orange-600"> — fill the 8D tab first</span>}</label>
              {d.attachments.map((a) => (
                <label key={a.id} className="flex items-center gap-2 text-xs"><Checkbox checked={f.attachment_ids.includes(a.id)} onCheckedChange={() => toggleAtt(a.id)} data-testid={`reply-attach-${a.id}`} />{a.filename}</label>
              ))}
            </div>
            {d.emails.length > 0 && <p className="text-[11px] text-muted-foreground" data-testid="reply-previous">Previously sent: {d.emails.map((e) => `${new Date(e.at).toLocaleDateString('en-GB')} to ${e.to.join(', ')}`).join(' · ')}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reply-cancel-btn">Cancel</Button>
          <Button onClick={send} disabled={sending || !d || !f.to.trim()} data-testid="reply-send-btn">{sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Send to customer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
